import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaChevronRight, FaChevronLeft } from "react-icons/fa";
import { motion } from "framer-motion";

const TimeSlotSelector = ({
  selectedDateTime,
  setSelectedDateTime,
  selectedProfessionalId,
  onNext,
}) => {
  const containerRef = useRef(null);
  const dayRefs = useRef({});
  const [availableTimes, setAvailableTimes] = useState([]);
  const startHour = 9;
  const endHour = 17;
  const leadtimetopickup = 30 * 60 * 1000;
  const visibleCount = 3;

  const generateDates = (count = 60) => {
    const today = new Date();
    return Array.from({ length: count }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return date;
    });
  };

  const allDates = useMemo(() => generateDates(60), []);

  const initialIndex = useMemo(() => {
    if (!selectedDateTime) return 0;
    const selected = new Date(selectedDateTime).toDateString();
    return Math.max(
      allDates.findIndex((d) => d.toDateString() === selected),
      0
    );
  }, [allDates, selectedDateTime]);

  const [startIndex, setStartIndex] = useState(initialIndex);
  const visibleDates = allDates.slice(startIndex, startIndex + visibleCount);

  const [selectedDate, setSelectedDate] = useState(() => {
    return selectedDateTime
      ? new Date(selectedDateTime)
      : allDates[initialIndex];
  });

  const formatTime24 = (hour, minute) => {
    const h = hour.toString().padStart(2, "0");
    const m = minute.toString().padStart(2, "0");
    return `${h}:${m}`;
  };

  const generateTimeSlots = (date, interval = 30) => {
    const slots = [];
    const now = new Date();
    const nowPlusLead = new Date(now.getTime() + leadtimetopickup);

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const time = new Date(date);
        time.setHours(hour, minute, 0, 0);

        const isSameDay = date.toDateString() === now.toDateString();
        if (!isSameDay || time > nowPlusLead) {
          slots.push(formatTime24(hour, minute));
        }
      }
    }

    return slots;
  };

  const isDateAvailable = (date) => generateTimeSlots(date).length > 0;

  useEffect(() => {
    const slots = generateTimeSlots(selectedDate);
    setAvailableTimes(slots);
  }, [selectedDate, selectedProfessionalId]);

  // Scroll to selected day when startIndex or mount
  useEffect(() => {
    const selectedKey = selectedDate?.toDateString();
    const buttonRef = dayRefs.current[selectedKey];
    if (containerRef.current && buttonRef) {
      containerRef.current.scrollTo({
        left: buttonRef.offsetLeft - containerRef.current.offsetLeft,
        behavior: "smooth",
      });
    }
  }, [startIndex]);

  useEffect(() => {
    if (!selectedDateTime) {
      const firstAvailableDate = visibleDates.find((d) => isDateAvailable(d));
      if (firstAvailableDate) {
        setSelectedDate(firstAvailableDate);
      }
    }
  }, [startIndex, visibleDates]);

  const handleTimeSelect = (timeStr) => {
    const [hourStr, minuteStr] = timeStr.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    const dateTime = new Date(selectedDate);
    dateTime.setHours(hour, minute, 0);

    const localISOString = new Date(
      dateTime.toLocaleString("en-US", { timeZone: "Pacific/Auckland" })
    ).toISOString();

    setSelectedDateTime(localISOString);

    if (typeof onNext === "function") onNext();
  };

  const isSelected = (timeStr) => {
    if (!selectedDateTime) return false;

    const selected = new Date(selectedDateTime);
    const selectedTime = formatTime24(
      selected.getHours(),
      selected.getMinutes()
    );

    return (
      selected.toDateString() === selectedDate.toDateString() &&
      timeStr === selectedTime
    );
  };

  // const isSelected = (timeStr) => {
  //   if (!selectedDateTime) return false;

  //   const selected = new Date(selectedDateTime);

  //   // ✅ Only highlight if the selected date is one of the visible days
  //   const isVisible = visibleDates.some(
  //     (d) => d.toDateString() === selected.toDateString()
  //   );
  //   if (!isVisible) return false;

  //   const [t, modifier] = timeStr.split(" ");
  //   let [h, m] = t.split(":").map(Number);
  //   if (modifier === "pm" && h !== 12) h += 12;
  //   if (modifier === "am" && h === 12) h = 0;

  //   return selected.getHours() === h && selected.getMinutes() === m;
  // };

  const handleWeekChange = (newStartIndex) => {
    const newVisibleDates = allDates.slice(
      newStartIndex,
      newStartIndex + visibleCount
    );

    // If the current selectedDate is not in the new week, auto-pick first available day
    const isCurrentDateVisible = newVisibleDates.some(
      (d) => d.toDateString() === selectedDate.toDateString()
    );

    if (!isCurrentDateVisible) {
      const firstAvailableDate = newVisibleDates.find((d) =>
        isDateAvailable(d)
      );
      if (firstAvailableDate) {
        setSelectedDate(firstAvailableDate);
      }
    }

    setStartIndex(newStartIndex);
  };

  const slotVariant = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05, // staggered fade-in
        duration: 0.3,
        ease: "easeOut",
      },
    }),
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-4">Select Pick Up Time</h3>

      <div className="flex items-center justify-between mb-4 text-sm text-textGray">
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2" />
          {visibleDates[0]?.toLocaleString("default", { month: "long" })}{" "}
          {visibleDates[0]?.getFullYear()}
        </div>

        <div className="space-x-2">
          <button
            disabled={startIndex === 0}
            onClick={() =>
              handleWeekChange(Math.max(startIndex - visibleCount, 0))
            }
            className="px-2 py-2 rounded bg-textGray border border-border text-text"
          >
            <FaChevronLeft size={12} />
          </button>
          <button
            disabled={startIndex + visibleCount >= allDates.length}
            onClick={() => handleWeekChange(startIndex + visibleCount)}
            className="px-2 py-2 rounded bg-redlight border border-border text-text"
          >
            <FaChevronRight size={12} />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex space-x-2 overflow-x-auto pb-3 mb-4 scroll-smooth"
      >
        {visibleDates.map((date) => {
          const key = date.toDateString();
          const isSameDay =
            selectedDate?.toDateString() === key &&
            selectedDateTime &&
            new Date(selectedDateTime).toDateString() === key;

          const isDisabled = !isDateAvailable(date);

          return (
            <button
              key={key}
              ref={(el) => (dayRefs.current[key] = el)}
              onClick={() => {
                if (isDisabled) return;
                setSelectedDate(date);

                // if (
                //   selectedDateTime &&
                //   new Date(selectedDateTime).toDateString() !==
                //     date.toDateString()
                // ) {
                //   setSelectedDateTime(null); // 🧼 Clear if date doesn't match
                // }
              }}
              disabled={isDisabled}
              className={`w-12 h-12 rounded-full border flex flex-col items-center justify-center text-sm font-medium
                ${
                  isSameDay
                    ? "bg-redlight text-white"
                    : "bg-drylight text-text border-text"
                }
                ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
              `}
            >
              <span className="text-sm">{date.getDate()}</span>
              <span className="text-xs">
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </span>
            </button>
          );
        })}
      </div>

      <div key={`week-${startIndex}`} className="space-y-3">
        {availableTimes.map((timeStr, index) => (
          <motion.button
            key={timeStr}
            onClick={() => handleTimeSelect(timeStr)}
            custom={index}
            initial="hidden"
            animate="visible"
            variants={slotVariant}
            className={`w-full py-3 px-4 rounded-lg border border-border text-left ${
              isSelected(timeStr)
                ? "bg-redlight border-redlight text-white font-medium"
                : "bg-dry"
            }`}
          >
            {timeStr}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TimeSlotSelector;
